import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useWatch } from 'react-hook-form';
import {
  useDataProvider,
  useNotify,
  useSafeSetState,
  Form,
  PasswordInput,
  required,
  useLogout,
} from 'react-admin';

import AuthLayout from '../AuthLayout';
import InputGroup from '../InputGroup';
import ButtonGroup from '../ButtonGroup';
import PasswordPolicyChecklist from './PasswordPolicyChecklist';
import {
  RESET_PASSWORD_LINK_ERROR,
  ResetPasswordFormFields,
  buildResetPasswordRequest,
  getResetPasswordErrorMessage,
  loadResetPasswordPolicy,
  validatePasswordsMatch,
} from './resetPasswordForm';
import {
  DEFAULT_PASSWORD_POLICY,
  PasswordPolicy,
  isPasswordPolicyMet,
  validatePasswordPolicy,
} from '../../../utilities/passwordPolicy';

type SubmitButtonProps = {
  loading?: boolean;
  passwordPolicy: PasswordPolicy;
}

const SubmitButton = ({ loading, passwordPolicy }: SubmitButtonProps) => {
  const password: string = useWatch({ name: 'password' }) ?? '';

  return (
    <Button
      variant="contained"
      type="submit"
      disabled={loading || !isPasswordPolicyMet(password, passwordPolicy)}
    >
      Set new password
    </Button>
  );
};

const ResetPasswordPage = () => {
  const [loading, setLoading] = useSafeSetState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const logout = useLogout();
  const [passwordPolicy, setPasswordPolicy] = useSafeSetState<PasswordPolicy | null>(null);

  useEffect(() => {
    if (!token) {
      notify(RESET_PASSWORD_LINK_ERROR, { type: 'error' });
      setPasswordPolicy(DEFAULT_PASSWORD_POLICY);
      return undefined;
    }

    let cancelled = false;

    loadResetPasswordPolicy(async () => {
      const { data } = await dataProvider.create('auth/reset-password/policy', { data: { token } });
      return data;
    }).then((result) => {
      if (cancelled) return;

      if (result.status === 'invalidLink') {
        notify(RESET_PASSWORD_LINK_ERROR, { type: 'error' });
        setPasswordPolicy(DEFAULT_PASSWORD_POLICY);
        return;
      }

      setPasswordPolicy(result.policy);
    });

    return () => {
      cancelled = true;
    };
  }, [dataProvider, notify, setPasswordPolicy, token]);

  const validatePassword = useMemo(
    () => (passwordPolicy ? [required(), validatePasswordPolicy(passwordPolicy)] : []),
    [passwordPolicy],
  );

  const submit = useCallback(async (data: ResetPasswordFormFields) => {
    if (!token) {
      notify(RESET_PASSWORD_LINK_ERROR, { type: 'error' });
      return;
    }

    setLoading(true);

    try {
      await dataProvider.create('auth/reset-password', { data: buildResetPasswordRequest(data, token) });
      logout(undefined, undefined, false);
      notify('New password set successfully');
    } catch (error) {
      notify(getResetPasswordErrorMessage(error), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, logout, notify, setLoading, token]);

  if (!passwordPolicy) {
    return (
      <AuthLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress aria-label="Loading Password Policy" />
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Form onSubmit={submit} mode="onTouched">
        <Typography component="h1" variant="h4" align="center" gutterBottom>
          Set new password
        </Typography>
        <Typography component="h2" variant="body1" align="center">
          Choose your new password
        </Typography>
        <InputGroup>
          <PasswordInput
            source="password"
            label="New password"
            disabled={loading}
            fullWidth
            validate={validatePassword}
          />
          <PasswordPolicyChecklist source="password" policy={passwordPolicy} />
          <PasswordInput
            source="confirmPassword"
            label="Confirm new password"
            disabled={loading}
            fullWidth
            validate={[required(), validatePasswordsMatch]}
          />
        </InputGroup>
        <ButtonGroup>
          <SubmitButton loading={loading} passwordPolicy={passwordPolicy} />
        </ButtonGroup>
      </Form>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
